import { useTranslation } from 'next-i18next';
import { useRouter } from 'next-translate-routes/router';
import type { FC } from 'react';
import React, { useState } from 'react';

import { AdvertisementBankCard } from '@/components';
import { BestRatesListWrapper } from '@/components/BestRatesList/BestRatesListWrapper';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { SavingsAccountsCards } from '@/components/SavingsAccountsCardList/parts/SavingsAccountsCards';
import { useCmsContent } from '@/contexts/CmsContentContext';
import { getBankLogoURL } from '@/services/supabase';
import { useSearchStore } from '@/store';
import type { IBankDepositCard } from '@/types';
import { AnalyticsEvent } from '@/types/analytics';
import { capitalizeFirstLetter, cn } from '@/utils';
import { getSavingsAccountsBreadcrumbs } from '@/utils/breadcrumbs';
import { findCountryKey } from '@/utils/countries';

import { SkeletCard } from '../SkeletCard';

interface DepositsCardListProps {
  featured: IBankDepositCard[];
  allDeposits: IBankDepositCard[];
  onLoadMoreDeposits: () => Promise<void>;
  onLoadMoreFeaturedDeposits: () => Promise<void>;
  // eslint-disable-next-line react/no-unused-prop-types
  favoriteDeposits?: IBankDepositCard[];
  shouldLoadMore?: boolean;
  shouldLoadMoreFeatured?: boolean;
  className?: string;
}

export const SavingsAccountsCardList: FC<DepositsCardListProps> = ({
  featured,
  allDeposits,
  shouldLoadMore = true,
  className,
  onLoadMoreDeposits,
  onLoadMoreFeaturedDeposits,
  shouldLoadMoreFeatured,
}) => {
  const router = useRouter();
  const { t } = useTranslation();

  const [isMoreLoading, setIsMoreLoading] = useState(false);
  const [isMoreFeaturedLoading, setIsMoreFeaturedLoading] = useState(false);
  const { country: initialCountry } = useCmsContent();

  const { isLoading } = useSearchStore((state) => state);

  const loadMoreDeposits = async () => {
    try {
      setIsMoreLoading(true);
      await onLoadMoreDeposits();
    } finally {
      setIsMoreLoading(false);
    }
  };

  const loadMoreFeaturedDeposits = async () => {
    try {
      setIsMoreFeaturedLoading(true);
      await onLoadMoreFeaturedDeposits();
    } finally {
      setIsMoreFeaturedLoading(false);
    }
  };

  return (
    <div className={cn('mt-2 flex w-full flex-col items-center', className)}>
      {!isLoading ? (
        <>
          <AdvertisementBankCard
            bankName="Lidion Bank"
            annualFee={t('savings-accounts.annual-fee.free')}
            rate={t('ad.lidion.rate')}
            image={getBankLogoURL('bank3530MT')}
            comment={t('ad.lidion.comment')}
            brokerUrl="/lidion"
            cashback={null}
            className="mb-5 mt-2 w-full"
            country="Malta"
            primaryColor="#326BFF"
            directUrl="/lidion"
            blockViewEvent={AnalyticsEvent.lidionBlockView}
            openOnlineEvent={AnalyticsEvent.lidionClickOpenOnline}
            shareEvent={AnalyticsEvent.lidionClickShare}
            protectedUpToValue="€100K"
          />
          <Breadcrumbs
            breadcrumbs={getSavingsAccountsBreadcrumbs()}
            className="self-start"
          />
          <BestRatesListWrapper />
          {featured && featured.length !== 0 ? (
            <>
              <div className="mt-8 w-full items-start">
                <h2 className="h5">
                  {t('savings-accounts.products.partners')}
                </h2>
              </div>
              <SavingsAccountsCards
                isLoading={isMoreFeaturedLoading}
                deposits={featured}
                onLoadMore={loadMoreFeaturedDeposits}
                shouldLoadMore={shouldLoadMoreFeatured}
                className="pb-0"
              />
            </>
          ) : null}
          {allDeposits && allDeposits.length !== 0 && (
            <>
              <div className="mt-6 w-full items-start">
                <h5>
                  {t('savings-accounts.products.non-partners', {
                    country: t(
                      `database.countries.${
                        capitalizeFirstLetter(
                          findCountryKey(router.query.country as string),
                        ) || initialCountry.name
                      }`,
                    ),
                  })}
                </h5>
              </div>
              <SavingsAccountsCards
                isLoading={isMoreLoading}
                deposits={allDeposits}
                onLoadMore={loadMoreDeposits}
                shouldLoadMore={shouldLoadMore}
                className="pb-20"
              />
            </>
          )}
        </>
      ) : (
        <div className="flex flex-col gap-4 py-5">
          {Array(3)
            .fill(0)
            .map((_, index) => (
              <SkeletCard key={index} />
            ))}
        </div>
      )}
    </div>
  );
};
